import type {
  BackfillExecutions,
  BackfillPagination,
} from '@/features/backfill/types/backfill.types.ts';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  getBackfillExecutions,
  postBackfillData,
} from '@/features/backfill/store/backfill.thunk.ts';

interface BackfillState {
  executions: BackfillExecutions[] | null;
  loading: boolean;
  error: string | null;
  pagination: BackfillPagination;
}

const initialState: BackfillState = {
  executions: [],
  loading: false,
  error: null,
  pagination: {
    count: 0,
    size: 0,
    page: 1,
  },
};

const backfillSlice = createSlice({
  name: 'backfill',
  initialState,
  reducers: {
    setPagination: (
      state: BackfillState,
      action: PayloadAction<{ page?: number; size?: number }>
    ) => {
      if (action.payload.page) {
        state.pagination.page = action.payload.page;
      }
      if (action.payload.size) {
        state.pagination.size = action.payload.size;
      }
    },
    clearBackfill: (state: BackfillState) => {
      state.executions = [];
      state.pagination.count = 0;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getBackfillExecutions.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBackfillExecutions.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const payload =
          action.payload as import('@/features/backfill/types/backfill.types').BackfillExecutionsResponse;
        state.executions = payload.results;
        state.pagination.count = payload.count;
        state.pagination.size = payload.size;
        state.pagination.page = payload.page;
      })
      .addCase(getBackfillExecutions.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || 'Failed to fetch backfill execution';
        state.executions = [];
        state.pagination = initialState.pagination;
      })
      .addCase(postBackfillData.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(postBackfillData.fulfilled, state => {
        state.loading = false;
        state.error = null;
      })
      .addCase(postBackfillData.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to post backfill';
      });
  },
});

export const { setPagination, clearBackfill } = backfillSlice.actions;
export default backfillSlice.reducer;
